import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  Typography,
} from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { QuestionOptInDetails, IOptInResponse, IOptInConfig, IOptInCheckboxes } from "../../../domain/ballot-opt-in";

interface QuestionOptInFormProps {
  opt_in?: boolean;
  opt_in_config: IOptInConfig;
  opt_in_details?: any;
  setOptIn: (optIn: IOptInResponse) => void
}

const buildInitialCheckboxesState = (opt_in_details: QuestionOptInDetails, checkboxes?: IOptInCheckboxes) => {
  const init: any = {};
  if (!checkboxes) {
    return init;
  }
  for (const a of Object.values(checkboxes)) {
    init[a.text] = opt_in_details && opt_in_details[a.text];
  }
  return init;
}

const isAllCheckboxesValid = (checkboxResponses: any, checkboxes?: IOptInCheckboxes) => {
  if (!checkboxes) {
    return true;
  }
  for (const a of Object.values(checkboxes)) {
    if (a.required !== false && (!checkboxResponses || !checkboxResponses[a.text])) {
      return false;
    }
  }
  return true;
}

export const BallotOptIn = ({ opt_in_config, opt_in, setOptIn, opt_in_details }: QuestionOptInFormProps) => {
  const { title, description, checkboxes } = opt_in_config;

  const [openOptIn, setOpenOptIn] = useState(!opt_in);


  const [checkboxResponses, setCheckboxResponses] = useState<Record<string, boolean>>(
    buildInitialCheckboxesState(opt_in_details, checkboxes)
  );

  const valid = useMemo(() => {
    if (!isAllCheckboxesValid(checkboxResponses, checkboxes)) {
      return false;
    }
    return true;
  }, [checkboxes, opt_in_details, checkboxResponses]);

  const handleAgree = (text: string) => {
    setCheckboxResponses({ ...checkboxResponses, [text]: !checkboxResponses[text] });
  };

  const allCheckboxesValid = useMemo(() => isAllCheckboxesValid(checkboxResponses, checkboxes), [checkboxes, checkboxResponses])

  const handleOptIn = useCallback(() => {
    const details: QuestionOptInDetails = {};
    if (allCheckboxesValid && checkboxes) {
      for (const a of Object.values(checkboxes)) {
        details[a.text] = checkboxResponses ? "Agree" : "Disagree";
      }
    }
    setOptIn({
      opt_in: true,
      opt_in_details: details,
    });
    setOpenOptIn(false)
  }, [setOptIn, allCheckboxesValid]);

  const handleOptOut = useCallback(() => {
    const checkboxResponses: any = {};
    if (allCheckboxesValid && checkboxes) {
      for (const a of Object.values(checkboxes)) {
        checkboxResponses[a.text] = checkboxResponses ? "Agree" : "Disagree";
      }
    }
    setCheckboxResponses(checkboxResponses);
    setOptIn({
      opt_in: false,
    });

  }, [setOptIn, setCheckboxResponses, allCheckboxesValid]);

  if (!opt_in_config) {
    return null
  }




  return (<Accordion expanded={openOptIn || !opt_in}
    sx={{
      border: "solid 1px",
      borderColor: "#AAA",
      boxShadow: `0px 2px 1px -1px rgba(0,0,0,0.14),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)`,
      borderBottomLeftRadius: 4,
      borderBottomRightRadius: 4,
      marginBottom: 2,
      px: 1, py: 1
    }}
  >
    <AccordionSummary
      expandIcon={<ExpandMoreIcon />}
      onClick={() => setOpenOptIn(!openOptIn)}
      sx={{}}
    >
      <Typography variant="h6">{title}</Typography>
    </AccordionSummary>
    <AccordionDetails>
      <Grid container>
        <Grid item xs={12} mb={2}>
          <Typography mb={3} sx={{ whiteSpace: 'pre-line' }}>{description}</Typography>

          {checkboxes ? Object.values(checkboxes).map(a => (
            <Grid item xs={12} mb={1}>
              <FormControlLabel
                control={<Checkbox checked={!!checkboxResponses[a.text]} onChange={() => handleAgree(a.text)} disabled={opt_in} />}
                label={a.text}
              />
            </Grid>
          ))
            : null
          }
        </Grid>

        <Grid item xs={12} mt={1}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
            <Button color={"interface" as any} variant="outlined" onClick={handleOptOut} >
              Opt-Out
            </Button>
            <Button
              color={"interface" as any}
              variant="contained"
              disabled={!valid || opt_in}
              onClick={handleOptIn}
            >
              Opt-In
            </Button>
          </Box>
        </Grid>
      </Grid>
    </AccordionDetails>
  </Accordion>)
};



const CheckboxesText = ({ agreement }: { agreement: Array<{ text: string }> }) => {
  return <>{agreement.map((item) => <Typography key={item.text}>{item.text}</Typography>)}</>
}